// Thread-group definitions are tenant-wide shared settings served by the backend
// (GET /api/threads/overview and GET/PUT /api/settings/messages, collection global_settings).
// localStorage only warm-starts the UI before the first server response of a session.

import {
  DEFAULT_THREAD_GROUPS,
  GROUP_COLOR_PALETTE,
  THREAD_GROUP_FLAGS,
} from "@/lib/thread-groups";

const THREAD_GROUPS_STORAGE_KEY = "healo-thread-groups";
const THREAD_GROUPS_CHANGE_EVENT = "healo-thread-groups-change";

const VALID_COLOR_IDS = new Set(GROUP_COLOR_PALETTE.map((c) => c.id));
const VALID_FLAG_IDS = new Set(THREAD_GROUP_FLAGS.map((f) => f.id));

let inMemoryGroups = null;
let hasLoadedGroups = false;

function canUseBrowserStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeGroup(value) {
  if (!value || typeof value !== "object") return null;
  const id = String(value.id || "").trim();
  const name = String(value.name || "").trim();
  const colorId = String(value.colorId || "").trim();
  if (!id || !name) return null;
  // Always an array (never absent): the settings PUT must carry the flags key so the
  // server treats this client as authoritative instead of preserving stored flags.
  const flags = Array.isArray(value.flags)
    ? [...new Set(value.flags.map((f) => String(f || "").trim()).filter((f) => VALID_FLAG_IDS.has(f)))]
    : [];
  return {
    id,
    name,
    colorId: VALID_COLOR_IDS.has(colorId) ? colorId : GROUP_COLOR_PALETTE[0].id,
    flags,
  };
}

function normalizeGroupsList(value) {
  if (!Array.isArray(value)) return null;
  const seen = new Set();
  const result = [];
  for (const entry of value) {
    const normalized = normalizeGroup(entry);
    if (!normalized) continue;
    if (seen.has(normalized.id)) continue;
    seen.add(normalized.id);
    result.push(normalized);
  }
  return result;
}

function defaultGroupsCopy() {
  return DEFAULT_THREAD_GROUPS.map((g) => ({ ...g }));
}

function readGroupsFromStorage() {
  if (!canUseBrowserStorage()) {
    return defaultGroupsCopy();
  }
  const raw = window.localStorage.getItem(THREAD_GROUPS_STORAGE_KEY);
  if (!raw) {
    return defaultGroupsCopy();
  }
  try {
    const parsed = JSON.parse(raw);
    const list = normalizeGroupsList(parsed?.groups);
    if (!list) return defaultGroupsCopy();
    return list;
  } catch {
    return defaultGroupsCopy();
  }
}

function emitGroupsChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(THREAD_GROUPS_CHANGE_EVENT));
}

export function getThreadGroupsConfig() {
  if (!hasLoadedGroups) {
    inMemoryGroups = readGroupsFromStorage();
    hasLoadedGroups = true;
  }
  return inMemoryGroups;
}

export function setThreadGroupsConfig(nextGroups) {
  const normalized = normalizeGroupsList(nextGroups) ?? [];
  inMemoryGroups = normalized;
  hasLoadedGroups = true;

  if (canUseBrowserStorage()) {
    try {
      window.localStorage.setItem(
        THREAD_GROUPS_STORAGE_KEY,
        JSON.stringify({ groups: normalized }),
      );
    } catch {
      /* ignore quota / serialization errors */
    }
  }

  emitGroupsChange();
  return inMemoryGroups;
}

export function subscribeThreadGroups(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getThreadGroupsConfig());
  window.addEventListener(THREAD_GROUPS_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(THREAD_GROUPS_CHANGE_EVENT, handler);
  };
}

export function resetThreadGroupsConfigToDefaults() {
  return setThreadGroupsConfig(defaultGroupsCopy());
}

function groupsListsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every(
    (g, i) =>
      g.id === b[i].id &&
      g.name === b[i].name &&
      g.colorId === b[i].colorId &&
      (g.flags || []).join("|") === (b[i].flags || []).join("|"),
  );
}

/**
 * Adopt the server's shared thread groups (from the threads overview or the settings API).
 * Skips the change event (and the localStorage write) when nothing changed, so the periodic
 * overview refresh doesn't churn subscribers. A null/invalid list is ignored — the current
 * (possibly warm-start) config stays.
 */
export function hydrateThreadGroupsFromServer(list) {
  const normalized = normalizeGroupsList(list);
  if (!normalized) {
    return getThreadGroupsConfig();
  }
  if (groupsListsEqual(getThreadGroupsConfig(), normalized)) {
    return inMemoryGroups;
  }
  return setThreadGroupsConfig(normalized);
}
